import React, { ReactNode } from 'react';
import { PostHogProvider as OriginalPostHogProvider, usePostHog as useOriginalPostHog } from 'posthog-js/react';
import posthog from 'posthog-js';
import type { PostHogWrapper } from './posthogWrapper';
interface PostHogProviderProps {
  children: ReactNode;
  client: PostHogWrapper | typeof posthog;
}

// No-op PostHog implementation for CI
const noOpPostHog = {
  capture: () => {},
  isFeatureEnabled: () => false,
  get_distinct_id: () => 'ci-test-id',
  get_session_id: () => 'ci-session-id',
  setPersonProperties: () => {},
  group: () => {},
  register: () => {},
  debug: () => {},
  onFeatureFlags: (callback: () => void) => callback(),
  init: () => {},
  startSessionRecording: () => {},
  isNoOp: () => true
};
export const PostHogProvider: React.FC<PostHogProviderProps> = ({
  children,
  client
}) => {
  // In CI, just return children without the original provider
  if (process.env.CI) {
    return <>{children}</>;
  }

  // Safety check: Don't pass no-op client to real PostHog provider
  if ('isNoOp' in client && client.isNoOp?.()) {
    // eslint-disable-next-line no-console
    console.warn('PostHog: No-op client detected in non-CI environment. Skipping PostHog provider.');
    return <>{children}</>;
  }

  // In normal environments, use the provided client with original provider
  return <OriginalPostHogProvider client={client as typeof posthog} data-sentry-element="OriginalPostHogProvider" data-sentry-component="PostHogProvider" data-sentry-source-file="posthogProvider.tsx">{children}</OriginalPostHogProvider>;
};

// Custom hook that works in both CI and normal environments
export const usePostHog = () => {
  // Always call the hook (Rules of Hooks requirement)
  const originalPostHog = useOriginalPostHog();

  // Return appropriate implementation based on environment
  if (process.env.CI) {
    return noOpPostHog;
  }
  return originalPostHog;
};
export default PostHogProvider;